import { createFileRoute, Link } from '@tanstack/react-router';
import { Check, Sparkles, Users, Building2, ArrowRight, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ContentHeader } from "@/components/content";
import { FAQSection } from "@/components/content";

export const Route = createFileRoute('/info/pricing')({
  component: PricingPage,
  head: () => ({
    meta: [
      { title: 'Pricing | Matricks' },
      { name: 'description', content: 'Matricks is currently in beta. Get free early access by invitation and help shape the future of enterprise integration.' },
    ],
  }),
});

const betaFeatures = [
  "Full platform access",
  "Unlimited API calls during beta",
  "All integrations & connectors",
  "AI Agent builder",
  "Workflow automation",
  "Real-time analytics",
  "Community support",
  "Early adopter benefits",
];

const upcomingPlans = [
  {
    name: "Starter",
    description: "For small teams and startups",
    icon: Sparkles,
  },
  {
    name: "Professional",
    description: "For growing organizations",
    icon: Users,
  },
  {
    name: "Enterprise",
    description: "For large-scale deployments",
    icon: Building2,
  },
];

const faqItems = [
  {
    question: "Why is Matricks free during beta?",
    answer:
      "We're in the early stages of building the platform and want to work closely with early adopters to ensure we're building the right features. In exchange for your valuable feedback, you get free access to the full platform.",
  },
  {
    question: "How do I get an invitation?",
    answer:
      "You can request an invitation by contacting us through our contact form or by emailing hello@matricks.ai. We're gradually expanding access to ensure we can provide quality support to all users.",
  },
  {
    question: "What happens when beta ends?",
    answer:
      "Early beta users will receive special pricing and benefits when we launch our paid plans. We'll give you plenty of notice before any pricing changes, and you'll have the option to continue with a plan that fits your needs.",
  },
  {
    question: "Is my data safe during beta?",
    answer:
      "Absolutely. We take security seriously from day one. All data is encrypted, we follow industry best practices, and we're working towards SOC 2 compliance. Your production workloads are safe with us.",
  },
  {
    question: "What kind of support is available?",
    answer:
      "During beta, we offer community support through our Discord and GitHub. We also actively engage with users to gather feedback and help resolve issues quickly.",
  },
  {
    question: "Can I use Matricks in production?",
    answer:
      "Yes! While we're in beta, the platform is production-ready. Many of our beta users are already running production workloads. We recommend starting with non-critical workflows and expanding as you get comfortable.",
  },
];

function PricingPage() {
  return (
    <div className="py-16 sm:py-24">
      <div className="container px-6">
        {/* Header */}
        <ContentHeader
          title="Currently in Beta"
          description="We're building the future of enterprise integration and AI automation. Join our beta program and get free early access."
          centered
        />

        {/* Beta Access Card */}
        <div className="mt-16 max-w-2xl mx-auto">
          <div className="relative rounded-2xl border-2 border-primary bg-primary/5 p-8 sm:p-10">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
              <span className="rounded-full bg-primary px-4 py-1.5 text-sm font-semibold text-primary-foreground">
                Beta Access
              </span>
            </div>

            <div className="text-center">
              <h2 className="text-3xl font-bold">Free During Beta</h2>
              <p className="mt-3 text-muted-foreground">
                Get full access to all features while we're in beta. No credit card required.
              </p>
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {betaFeatures.map((feature, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-primary flex-shrink-0" />
                  <span className="text-sm">{feature}</span>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild>
                <Link to="/info/contact">
                  Request Invitation
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <a href="mailto:hello@matricks.ai">
                  <Mail className="mr-2 h-4 w-4" />
                  Email Us
                </a>
              </Button>
            </div>
          </div>
        </div>

        {/* Coming Soon Plans */}
        <div className="mt-24">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold">Plans Coming Soon</h2>
            <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
              We're working on flexible pricing plans that will suit teams of all sizes.
              Beta users will receive exclusive early adopter benefits.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
            {upcomingPlans.map((plan) => (
              <div
                key={plan.name}
                className="rounded-xl border bg-card p-6 text-center opacity-75"
              >
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-muted">
                  <plan.icon className="h-6 w-6 text-muted-foreground" />
                </div>
                <h3 className="mt-4 font-semibold">{plan.name}</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  {plan.description}
                </p>
                <p className="mt-4 text-xs text-muted-foreground">Coming soon</p>
              </div>
            ))}
          </div>
        </div>

        {/* Early Adopter Benefits */}
        <div className="mt-24 rounded-2xl bg-muted/50 p-8 sm:p-12">
          <div className="max-w-2xl mx-auto text-center">
            <Sparkles className="mx-auto h-10 w-10 text-primary" />
            <h2 className="mt-4 text-2xl font-bold">Early Adopter Benefits</h2>
            <p className="mt-4 text-muted-foreground">
              As a beta user, you're helping us shape the future of Matricks. When we launch paid plans,
              you'll receive:
            </p>
            <ul className="mt-6 space-y-3 text-left max-w-md mx-auto">
              <li className="flex items-start gap-3">
                <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span>Lifetime discount on all plans</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span>Priority access to new features</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span>Direct input on our product roadmap</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span>Recognition in our early adopter community</span>
              </li>
            </ul>
          </div>
        </div>

        {/* FAQ */}
        <FAQSection
          title="Beta FAQ"
          description="Common questions about our beta program."
          items={faqItems}
        />
      </div>
    </div>
  );
}
